/**
 * WikilinkMark — TipTap inline mark for `[[Target]]` and `[[Target|alias]]`.
 *
 * Ports the reference extension from
 * `with_obsidian/localvaultexplorer/packages/web/src/components/editor/extensions/WikilinkMark.ts`
 * to TipTap 3.x with our wikilink shape (`Target#section` permitted).
 *
 * The mark stores `target` (canonical wikilink target, e.g.
 * `Cases/Hadley v. Baxendale`), `alias` (displayed text, optional), and
 * `section` (deep-link section header, optional). Click handling is
 * wired by VaultEditorPane via a `data-target` lookup; the extension
 * itself stays UI-framework agnostic.
 */

import { Mark, mergeAttributes } from '@tiptap/core';

export interface WikilinkAttrs {
  target: string;
  alias: string | null;
  section: string | null;
}

const PATTERN = /\[\[([^\]|#\n]+?)(#[^\]|\n]+)?(?:\|([^\]\n]+))?\]\]/g;

export interface WikilinkOptions {
  /** Extra classes applied to the rendered <span>. */
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  // Augment Commands so `editor.commands.setWikilink` is type-safe.
  interface Commands<ReturnType> {
    wikilink: {
      setWikilink: (attrs: WikilinkAttrs) => ReturnType;
      unsetWikilink: () => ReturnType;
    };
  }
}

export const WikilinkMark = Mark.create<WikilinkOptions>({
  name: 'wikilink',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      target: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-target') ?? '',
        renderHTML: (attrs) => ({ 'data-target': attrs.target ?? '' }),
      },
      alias: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-alias'),
        renderHTML: (attrs) =>
          attrs.alias ? { 'data-alias': attrs.alias } : {},
      },
      section: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-section'),
        renderHTML: (attrs) =>
          attrs.section ? { 'data-section': attrs.section } : {},
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span.vault-wikilink',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        { class: 'vault-wikilink' },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ];
  },

  addCommands() {
    return {
      setWikilink:
        (attrs) =>
        ({ commands }) =>
          commands.setMark(this.name, attrs),
      unsetWikilink:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },

  // Match-and-mark on input so typing `]]` finalizes a wikilink.
  // We deliberately don't define a paste rule because pastes go through
  // the markdown-to-doc parser (see toProseDoc.ts).
});

/**
 * Tokenize a string into wikilink + plain-text spans. Useful for the
 * markdown-to-doc converter that runs on note open.
 */
export interface WikilinkToken {
  kind: 'text' | 'wikilink';
  value: string;
  attrs?: WikilinkAttrs;
}

export function tokenizeWikilinks(input: string): WikilinkToken[] {
  const out: WikilinkToken[] = [];
  let cursor = 0;
  PATTERN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PATTERN.exec(input)) !== null) {
    if (cursor < m.index) {
      out.push({ kind: 'text', value: input.slice(cursor, m.index) });
    }
    const target = m[1]!.trim();
    const section = m[2] ? m[2].slice(1).trim() : null;
    const alias = m[3] ? m[3].trim() : null;
    out.push({
      kind: 'wikilink',
      value: alias ?? target,
      attrs: { target, alias, section },
    });
    cursor = m.index + m[0].length;
  }
  if (cursor < input.length) {
    out.push({ kind: 'text', value: input.slice(cursor) });
  }
  if (out.length === 0) out.push({ kind: 'text', value: input });
  return out;
}
